<template>
  <form
    id="symbol-input-form"
    class="interactive-tour-container"
    @submit.prevent="submitNewSymbol()"
  >
    <label
      for="symbol-input"
      id="symbol-input-label"
      class="sc3-form-label"
    >SYMBOL</label>
    <input
      type="text"
      placeholder="Enter Symbol"
      id="symbol-input"
      ref="symbolInputElement"
      :value="symbol"
      @input="handleInput($event)"
      @keydown.enter="submitNewSymbol()"
      @keyup="handleInputKeyUp($event)"
      @paste="handleInputPaste()"
      @focusout="handleCloseSuggestionBox()"
      @blur="handleInputBlur()"
      autocomplete="off"
    />
    <span id="symbol-input-right-side-button">
      <button
        v-if="isUserTypingNewSymbol"
        type="submit"
        ref="symbolInputSubmitButtonElement"
      >Go</button>
      <RefreshButton v-else />
    </span>
    <div class="symbol-autosuggest">
      <ul
        class="dropdown-menu"
        :class="{ 'dropdown-menu-open': isSuggestionsBoxOpen && symbol && suggestions.length }"
        ref="suggestionDropDownNode"
        @focusout="handleCloseSuggestionBox()"
      >
        <li
          class="dropdown-menu-option"
          v-for="(suggestion, index) in suggestions"
          :key="suggestion.symbol"
          :class="{ 'selected': index === selectedSuggestionIndex }"
        >
          <button
            type="button"
            :title="suggestion.symbol"
            class="dropdown-menu-option-button"
            @mousedown="handleSymbolSuggestionButtonMouseDown(suggestion.symbol)"
          >
            <div class="suggestion-container">
              <div v-html="suggestion.highlightedSymbol"></div>
              <div v-html="suggestion.highlightedCompany"></div>
              <div class="exchange">{{ suggestion.exchange }}</div>
            </div>
          </button>
        </li>
        <li
          v-if="suggestions.length === MAX_AUTO_SUGGEST_RESULTS"
          class="dropdown-menu-option"
          :class="{ 'selected': selectedSuggestionIndex === suggestions.length }"
        >
          <button
            type="button"
            class="dropdown-menu-option-button"
            @mousedown="showMoreResultsFor(lastTypedSymbol)"
          >
            <div class="suggestion-container more-results-container">
              More Results For "{{ lastTypedSymbol.toUpperCase() }}" ...
            </div>
          </button>
        </li>
      </ul>
    </div>
    <InteractiveTourWindow
      :page-number="0"
      :position="{ left: (dimensions) => `calc(50% - ${dimensions.baseWidth}px)` }"
      :arrow-position="InteractiveTourTrianglePosition.TopLeft"
    >
      <template v-slot:main-header>
        <span>Create a new chart for any symbol and customize it with the period, type, range, and more</span>
      </template>
      <template v-slot:explanation>
        <div>
          <p>Use the input box to enter a symbol of your choice, then set the Period and Range for your chart.</p>
          <p>You can also click the refresh icon to update your chart's data.</p>
        </div>
      </template>
    </InteractiveTourWindow>
  </form>
</template>

<script lang="ts" setup>
  import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
  import { storeToRefs } from 'pinia';
  import { useChartStore } from '@/stores/chartStore';
  import { useLayoutStore } from '@/stores/layoutStore';
  import { useChartNotesV2 } from '@/composables/useChartNotesV2';
  import { AutoSuggestEntry, useSymbolSuggestions } from '@/composables/useSymbolSuggestions';
  import { InteractiveTourTrianglePosition } from '@/composables/useInteractiveTour';
  import { usePerfChartSymbols } from '@/composables/usePerfChartSymbols';
  import { isAnHtmlUIElementFocused } from '@/utils/isAnHtmlUIElementFocused';
  import { waitForDomToRepaint } from '@/utils/waitForDomToRepaint';
  import { wait } from '@/utils/wait';
  import RefreshButton from '@/components/buttons/RefreshButton.vue';
  import InteractiveTourWindow from '@/components/page-skeleton/InteractiveTourWindow.vue';
  import { CHART_TYPE_OPTION_PERF_CHART } from '@/enums/chart-type-options';
  import DEFAULT_CHART from '@/default-chart.json';

  interface SuggestionHtml extends AutoSuggestEntry {
    highlightedCompany: string;
    highlightedSymbol: string;
  };

  const chartStore = useChartStore();
  const layoutStore = useLayoutStore();
  const { symbol: chartStoreSymbol } = storeToRefs(chartStore);
  const { isActivatingAnnotationMode, isAnnotating } = useChartNotesV2();
  const { getSuggestions, getHighlightedHtmlFromComparison } = useSymbolSuggestions();
  const { MAXIMUM_NUMBER_OF_PERF_CHART_SYMBOLS, getPerfChartSymbols } = usePerfChartSymbols();
  const symbolInputElement = ref<HTMLInputElement>();
  const symbolInputSubmitButtonElement = ref<HTMLButtonElement>();
  const symbol = ref(chartStoreSymbol.value);
  const isSuggestionsBoxOpen = ref(false);
  const suggestionDropDownNode = ref<HTMLUListElement>();
  const suggestions = ref<SuggestionHtml[]>([]);
  const POSSIBLE_CHARACTERS = [
    'Backspace', '.', '$', '/', '^', '!', '&'
  ];
  const MAX_AUTO_SUGGEST_RESULTS = 10;
  const lastTypedSymbol = ref(chartStoreSymbol.value);
  const selectedSuggestionIndex = ref(-1);
  const isUserTypingNewSymbol = computed(() => {
    return symbol.value !== chartStoreSymbol.value;
  });

  function handleInput(e: Event) {
    if (!(e.target instanceof HTMLInputElement)) {
      return;
    }

    symbol.value = e.target.value;
  }

  function handleInputBlur() {
    window.setTimeout(handleCloseSuggestionBox, 0);
  }

  function handleCloseSuggestionBox() {
    symbol.value = lastTypedSymbol.value;
    selectedSuggestionIndex.value = -1;
    isSuggestionsBoxOpen.value = false;
  }

  function updateLastTypedSymbol() {
    lastTypedSymbol.value = symbol.value;
  }

  async function changeSuggestions() {
    if (symbol.value === lastTypedSymbol.value) {
      return;
    }

    updateLastTypedSymbol();

    isSuggestionsBoxOpen.value = true;
    selectedSuggestionIndex.value = -1;
    const valueString = sanitizeInput(symbol.value);

    if (!valueString) {
      suggestions.value = [];
      return;
    }

    const fetchedSuggestions = await getSuggestions(valueString);
    const suggestionHtmlMap = getHighlightedHtmlFromComparison(valueString, fetchedSuggestions);

    suggestions.value = suggestionHtmlMap;
  }

  async function handleInputKeyUp(e: KeyboardEvent) {
    if (e.key === 'Enter') {
      return;
    }

    if (e.key === 'ArrowDown') {
      next();
      return;
    }

    if (e.key === 'ArrowUp') {
      previous();
      return;
    }

    await changeSuggestions();
  }

  async function handleInputPaste() {
    // paste event fires before input element's value (i.e. text) actually changes
    // the await wait(0) below ensures that the input element's value (i.e. text) will have changed by the time we call changeSuggestions
    await wait(0);
    await changeSuggestions();
  }

  function next() {
    if (
      !suggestions.value.length ||
      selectedSuggestionIndex.value >= suggestions.value.length
    ) {
      selectedSuggestionIndex.value = -1;
    } else {
      selectedSuggestionIndex.value++;
    }

    fillSearchBox();
  }

  function previous() {
    if (!suggestions.value.length) {
      selectedSuggestionIndex.value = -1;
    } else if (selectedSuggestionIndex.value >= 0) {
      selectedSuggestionIndex.value--;
    } else {
      selectedSuggestionIndex.value = suggestions.value.length;
    }

    fillSearchBox();
  }

  function fillSearchBox() {
    const selectIndex = selectedSuggestionIndex.value;

    if (selectIndex === -1 || !suggestions.value[selectIndex]) {
      symbol.value = lastTypedSymbol.value;
      return;
    }

    const suggestedSymbol = suggestions.value[selectIndex].symbol;
    const trimmedSymbol = suggestedSymbol.trim();
    const iComma = trimmedSymbol.lastIndexOf(',');
    const iColon = trimmedSymbol.lastIndexOf(':');
    const hasComma = iComma !== -1 && iComma > iColon;
    const hasColon = iColon !== -1 && iColon > iComma;

    if (hasComma) {
      const symbols = trimmedSymbol.split(',');

      symbols.pop();
      symbols.push(suggestedSymbol);

      symbol.value = symbols.join(',');
      return;
    }

    if (hasColon) {
      const symbols = trimmedSymbol.split(':');

      symbols.pop();
      symbols.push(suggestedSymbol);

      symbol.value = symbols.join(':');
      return;
    }

    symbol.value = suggestedSymbol;
  }

  function showMoreResultsFor(symbolToSearchFor: string) {
    window.location.href = `/search/?section=symbol&q=${symbolToSearchFor}`;
  }

  function handleSymbolSuggestionButtonMouseDown(suggestedSymbol: string) {
    symbol.value = suggestedSymbol;

    updateLastTypedSymbol();
    submitNewSymbol();
  }

  function submitNewSymbol() {
    if (
      isActivatingAnnotationMode.value ||
      isAnnotating.value
    ) {
      suggestions.value = [];
      symbolInputElement.value?.blur();
      symbolInputSubmitButtonElement.value?.blur();
      return;
    }

    const formattedSymbol = sanitizeInput(symbol.value) || sanitizeInput(chartStore.symbol) || sanitizeInput(DEFAULT_CHART.symbol);
    let perfChartSymbols = getPerfChartSymbols(formattedSymbol).slice(0, MAXIMUM_NUMBER_OF_PERF_CHART_SYMBOLS);
    const mainChartSymbol = perfChartSymbols.at(0)?.symbol ?? '';
    perfChartSymbols = perfChartSymbols.slice(1);

    if (mainChartSymbol) {
      if (perfChartSymbols.length) {
        chartStore.$patch({
          symbol: mainChartSymbol,
          chartType: CHART_TYPE_OPTION_PERF_CHART.value,
          perfChartSymbols,
          latestLoadPerfChartTimeStamp: Date.now()
        });
      } else {
        chartStore.$patch({ symbol: mainChartSymbol });
      }
    }

    const isShowMore = !!suggestions.value.length && selectedSuggestionIndex.value === suggestions.value.length;

    if (isShowMore) {
      showMoreResultsFor(mainChartSymbol);
      return;
    }

    symbol.value = mainChartSymbol;
    lastTypedSymbol.value = mainChartSymbol;
    suggestions.value = [];
    symbolInputElement.value?.blur();
    symbolInputSubmitButtonElement.value?.blur();
  }

  function sanitizeInput(input: string) {
    return input.replace(/\\/g, '')
      .replace(/\s/g, '')
      .toUpperCase();
  }

  function isValidKey(key: string) {
    if (typeof key !== 'string') {
      return false;
    }

    return (
      (key.length === 1 && /[a-zA-Z0-9_]/.test(key)) ||
      POSSIBLE_CHARACTERS.includes(key)
    );
  }

  function handleHtmlBodyKeyDown(e: KeyboardEvent) {
    // Only give the input element focus if the user isn't
    // already focused on something else important on the page
    if (
      e.ctrlKey ||
      e.metaKey ||
      e.altKey ||
      isAnHtmlUIElementFocused() ||
      layoutStore.modalType ||
      layoutStore.isDragNDropping ||
      isActivatingAnnotationMode.value ||
      isAnnotating.value ||
      !isValidKey(e.key)
    ) {
      return;
    }

    symbolInputElement.value?.focus({ preventScroll: true });

    isSuggestionsBoxOpen.value = true;
  }

  async function handleNewChartStoreSymbol(newSymbol: string) {
    symbol.value = newSymbol;
    lastTypedSymbol.value = newSymbol;
    suggestions.value = [];
    selectedSuggestionIndex.value = -1;

    symbolInputElement.value?.focus({ preventScroll: true });

    await Promise.all([
      waitForDomToRepaint(),
      wait(0)
    ]);

    handleCloseSuggestionBox();

    symbolInputElement.value?.select();
  }

  onMounted(() => {
    handleNewChartStoreSymbol(chartStoreSymbol.value);
    document.body.addEventListener('keydown', handleHtmlBodyKeyDown);

    onUnmounted(() => {
      document.body.removeEventListener('keydown', handleHtmlBodyKeyDown);
    });
  });

  watch(chartStoreSymbol, handleNewChartStoreSymbol);
</script>

<style scoped>
  #symbol-input-form {
    display: inline-block;
    vertical-align: top;
    white-space: nowrap;
  }

  #symbol-input-label {
    margin: 0px 0px 1px 3px;
    font-size: 10px;
    line-height: 1;
  }

  #symbol-input {
    display: inline-block;
    text-transform: uppercase;
    vertical-align: top;
    font-size: 13px;
    font-weight: 700;
    width: 96px;
    height: var(--sc3-fixed-height);
    padding: 0px 0px 0px 8px;
    margin: 0px;
    border: 2px solid #c0ced8;
    border-right: none;
    border-radius: 4px 0px 0px 4px;
    color: #707f8f;
    transition: all 0.35s ease;
  }

  #symbol-input:hover,
  #symbol-input:active,
  #symbol-input:focus {
    border-color: #707f8f;
    color: #2d3339;
  }

  #symbol-input-right-side-button {
    display: inline-block;
    vertical-align: top;
  }

  #symbol-input-right-side-button button {
    display: inline-block;
    vertical-align: top;
    color: #707f8f;
    font-weight: 700;
    font-size: 13px;
    padding: 0px 8px;
    background-color: white;
    transition: all 0.35s ease;
    border: 2px solid #c0ced8;
    border-left: none;
    margin: 0px;
    touch-action: manipulation;
    user-select: none;
    border-radius: 0px 4px 4px 0px;
    height: var(--sc3-fixed-height);
  }

  #symbol-input-right-side-button button:hover,
  #symbol-input-right-side-button button:active,
  #symbol-input-right-side-button button:focus {
    color: #3281d0;
  }

  #symbol-input:hover + #symbol-input-right-side-button button,
  #symbol-input:active + #symbol-input-right-side-button button,
  #symbol-input:focus + #symbol-input-right-side-button button {
    border-color: #707f8f;
  }

  .symbol-autosuggest {
    position: relative;
  }

  .suggestion-container {
    display: flex;
  }

  .more-results-container {
    font-weight: normal;
    font-style: italic;
  }

  .suggestion-container > * {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .suggestion-container > :nth-child(2) {
    flex: 5;
    width: 400px;
  }

  .suggestion-container > :first-child {
    flex: 2;
  }

  .suggestion-container > :last-child {
    text-align: right;
    flex: 1;
  }

  .suggestion-container div >>> b {
    color: black;
    font-weight: bold;
  }

  .exchange {
    font-weight: normal;
  }

  li.dropdown-menu-option.selected {
    background-color: #c0ced8 !important;
  }

  .dropdown-menu-option {
    cursor: pointer;
  }
</style>
